const { Core, Utils, Events } = Global;
const { $fetch, parseHTML } = Utils;

customElements.define('cart-provider', class extends Core {
    liveSections = new Set();

    GIFT_CONFIG = {
        variantId: 57049480364416,
        qty: 1,
        threshold: 50000 // $500
    };

    // Checkout wrappers (cart drawer + cart page sidebar)
    CHECKOUT_WRAPPERS = '#cart-drawer-live-region-checkout, #cart-sidebar-live-region-checkout';
    CHECKOUT_BUTTONS = '#cart-drawer-live-region-checkout button[name="checkout"], #cart-sidebar-live-region-checkout button[name="checkout"]';

    _giftPending = false;   // true while gift logic is running
    _minQtyBlocked = false; // existing min-qty rule

    render() {
        this.sub(Events.CART_REGISTER, this._registerSection, { global: true });
        this.sub(Events.CART_ADD, this._addToCart, { global: true });
        this.sub(Events.CART_CHANGE, this._updateCart, { global: true });

        // Safety net: block checkout clicks/submits while gift logic is pending
        const guard = (e) => {
            if (!this._giftPending) return;
            const btn = e.target && e.target.closest && e.target.closest('button[name="checkout"]');
            const isCheckoutSubmit = e.type === 'submit' && e.submitter && e.submitter.name === 'checkout';
            if (btn || isCheckoutSubmit) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };
        document.addEventListener('click', guard, true);
        document.addEventListener('submit', guard, true);

        // Check gift on page load (e.g. customer lands on /cart below threshold without gift)
        const initialCheck = () => {
            this._lockCheckout();
            this._handleGiftLogic()
                .catch(err => console.error(err))
                .finally(() => this._unlockCheckout());
        };

        if (document.readyState === 'complete') {
            setTimeout(initialCheck, 0);
        } else {
            window.addEventListener('load', initialCheck, { once: true });
        }
    }

    _registerSection(section) {
        this.liveSections.add(section);
    }

    // ---------- Checkout lock helpers ----------

    _lockCheckout() {
        this._giftPending = true;
        this._applyCheckoutState();
    }

    _unlockCheckout() {
        this._giftPending = false;
        this._applyCheckoutState();
    }

    _applyCheckoutState() {
        const disabled = this._giftPending || this._minQtyBlocked;

        const apply = () => {
            document.querySelectorAll(this.CHECKOUT_WRAPPERS).forEach(el => {
                el.classList.toggle('disabled', disabled);
            });
            document.querySelectorAll(this.CHECKOUT_BUTTONS).forEach(btn => {
                btn.disabled = disabled;
                btn.setAttribute('aria-busy', this._giftPending ? 'true' : 'false');
            });
        };

        apply();
        // Run again after the live region has re-rendered
        requestAnimationFrame(apply);
    }

    // ---------- Gift logic ----------

    async _handleGiftLogic() {
        const { variantId, qty, threshold } = this.GIFT_CONFIG;

        const cart = await fetch('/cart.js').then(res => res.json());
        const giftItem = cart.items.find(item => item.variant_id === variantId);

        let totalWithoutGift = cart.total_price;
        if (giftItem) {
            totalWithoutGift -= giftItem.final_line_price;
        }

        console.log('totalWithoutGift: ', totalWithoutGift);

        // ---- Cart empty → remove gift ----
        if (totalWithoutGift <= 0) {
            if (giftItem) {
                await this._requestCart('cartChange', {
                    id: giftItem.key,
                    quantity: 0
                }, [variantId]);
            }
            return;
        }

        // ---- Business rule ----
        // BELOW $500 → ADD gift
        // ABOVE $500 → REMOVE gift
        if (totalWithoutGift >= threshold) {
            if (giftItem) {
                await this._requestCart('cartChange', {
                    id: giftItem.key,
                    quantity: 0
                }, [variantId]);
            }
        } else {
            if (!giftItem) {
                await this._requestCart('cartAdd', {
                    items: [{
                        id: variantId,
                        quantity: qty
                    }]
                }, [variantId]);
            } else if (giftItem.quantity !== qty) {
                await this._requestCart('cartChange', {
                    id: giftItem.key,
                    quantity: qty
                }, [variantId]);
            }
        }
    }

    // ---------- Cart actions ----------

    async _addToCart({ items }) {
        this._lockCheckout();

        try {
            var item = $(`.min_qty.p_var_min-${items[0].id}`);
            var qty = items[0].quantity * 1;
            var min_qty = item.data('minqty') * 1;
            var type = item.data('type');

            if (type === 'minimum_order') {
                if (min_qty > qty) {
                    qty = min_qty;
                }
            } else if (type === 'sale_unit') {
                // Round up to the nearest multiple of min_qty
                if (qty % min_qty !== 0) {
                    qty = Math.ceil(qty / min_qty) * min_qty;
                }
            }

            items[0].quantity = qty;

            await this._requestCart('cartAdd', {
                items,
                attributes: {
                    notification_items_variant_ids: items.map(({ id }) => id).join(',')
                }
            }, items.map(item => item.id));

            await this._handleGiftLogic();
        } catch (error) {
            console.error(error);
        } finally {
            // Always unlock, even if something failed
            this._unlockCheckout();
        }

        this._cleanCartAttributes();
    }

    async _updateCart({ key, quantity, selling_plan }) {
        // TODO: deprecate implicit variantId
        const [variantId] = key.split(':');
        const isGift = Number(variantId) === this.GIFT_CONFIG.variantId;

        if (!isGift) this._lockCheckout();

        try {
            await this._requestCart('cartChange', {
                id: key, quantity, selling_plan,
            }, [variantId]);

            // If the update is for the gift itself → stop
            if (isGift) return;

            await this._handleGiftLogic();
        } catch (error) {
            console.error(error);
        } finally {
            if (!isGift) this._unlockCheckout();
        }
    }

    _cleanCartAttributes() {
        this._fetchCart('cartUpdate', { attributes: { notification_items_variant_ids: null } });
    }

    _fetchCart(route, body) {
        return fetch(`${window.dynamicURLs[route]}.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(body)
        });
    }

    async _requestCart(route, body, items, keys) {
        try {
            const res = await this._fetchCart(route, {
                ...body,
                sections: Array.from(this.liveSections).join(','),
            });

            const cartData = await res.json();

            if (!res.ok) {
                this.pub(Events.TOAST_NOTIFICATION, {
                    type: 'error',
                    msg: cartData.description,
                    title: cartData.message
                });
                throw new Error(cartData.description);
            }

            this.pub(Events.CART_UPDATE, {
                sections: cartData.sections,
                req: route,
                items,
                keys
            });

            // ---- Min-qty popup logic ----
            if ($('.variant_metafields').length > 0) {
                var type = $('.variant_metafields').data('type');
                var title = $('.variant_metafields').data('title');
                var qty = $('.variant_metafields').data('minqty');
                $('.popupMinItemQty').text(qty);
                $('.popupPoductTitle').text(title);
                $('.pop-text').hide();
                $(type).show();
                $('.sweet-alert').show();
                this._minQtyBlocked = true;
            } else {
                $('.sweet-alert').hide();
                this._minQtyBlocked = false;
            }

            // Re-apply after every re-render (respects gift lock + min qty)
            this._applyCheckoutState();

            var text = $('.item-0_qty').text();
            if (text !== '') {
                $('.cart-min-msg').show();
                $('.cart-min-msg .alert--content').text(text);
            } else {
                $('.cart-min-msg').hide();
            }

        } catch (error) {
            console.error(error);
            this.pub(Events.CART_ERROR, error);
        }
    }
});

customElements.define('cart-qty', class extends Core {
    propTypes = {
        value: Number,
        key: String,
        input: Boolean,
        'prevent-default': Boolean
    }

    render() {
        this.$({
            [this.evt]: this._handleEvent
        });
    }

    _handleEvent(e) {
        this.prop('prevent-default') && e.preventDefault();
        this.pub(Events.CART_CHANGE, {
            key: this.prop('key'),
            quantity: this._getValue(e)
        });
    }

    _getValue(e) {
        return this.evt === 'change'
            ? e.target.value
            : this.prop('value');
    }

    get evt() {
        return this.prop('input')
            ? 'change'
            : 'click';
    }
});

customElements.define('cart-live-region', class extends Core {
    propTypes = {
        'target-section': String,
        'target-element': String
    }

    render() {
        this.targetSection = this.prop('target-section') || this.sectionId;
        this.targetSelector = this.prop('target-element') || `#${this.id}`;

        customElements.whenDefined('cart-provider').then(() => {
            this.pub(Events.CART_REGISTER, this.targetSection);
        });

        this.sub(Events.CART_UPDATE, this._updateContent, { global: true });
    }

    _updateContent({ sections }) {
        const targetSection = sections && sections[this.targetSection];
        if (!targetSection) {
            console.error(`target section ${this.targetSection} does not exist`);
            return;
        }

        const doc = Utils.parseHTML(targetSection);
        Utils.$replaceContent(this, doc.querySelector(this.targetSelector));
    }
});
